import { useState, useEffect, useMemo } from "react";
import Icon from "../Icon";
import { AvatarOdoo } from "./SelectorUsuario";
import * as api from "../../api";
import { rangoSemana, rangoSemanaCorto } from "./rangoSemana";
import { JORNADA_DEFECTO, objetivoDia, fmtH } from "../../jornada";
import { lineaJornada, explicarMotivo } from "./textoJornada";
import { hhmm } from "../../utils/hora";

// Cuadro de mandos de MI jornada, semana a semana, con las horas que tiene
// Odoo.
//
// La fuente es hr.attendance y no fichajes_log a propósito: el registro local
// solo guarda lo fichado desde ALM, y en Odoo está además lo anterior y lo que
// corrige administración a mano, que es lo que acaba contando.

const MESES = ["enero","febrero","marzo","abril","mayo","junio",
               "julio","agosto","septiembre","octubre","noviembre","diciembre"];
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ymd = d => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Madrid" }).format(d);

// Lunes de la semana a la que pertenece esa fecha. La semana laboral española
// empieza en lunes y getDay() empieza en domingo, de ahí el +6 %7.
function lunesDe(fecha) {
  const d = new Date(fecha);
  d.setHours(12, 0, 0, 0);                       // mediodía: inmune al cambio de hora
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const sumarDias = (fecha, n) => {
  const d = new Date(fecha);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return d;
};

const fmtHoras = h => {
  const m = Math.max(0, Math.round(h * 60));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

// El saldo lleva signo siempre: "+2h 10m" y "−1h 05m" se leen de un vistazo,
// "2h 10m" a secas no dice si sobran o faltan.
const fmtSaldo = h => `${h < 0 ? "−" : "+"}${fmtHoras(Math.abs(h))}`;

const CLAVE_PLEGADO = "alm_horas_semana_plegado";

// El mes y la semana ya no son de este panel: los manda la barra de controles de
// la pestaña, que es la misma que mueve el calendario de abajo. Antes cada mitad
// de la pantalla tenia su propio mes y sus propias flechas, y se leian dos
// periodos distintos como si fueran uno.
//
// comparativa —cuando hay equipos seleccionados— pone al lado de mi barra UNA
// POR PERSONA de la gente que se compara, con su foto de Odoo al pie. Empezo
// siendo una sola barra con la media del equipo y no servia: "la media" no dice
// a quien mirar, y la pregunta que se hace aqui es siempre sobre alguien.
//
// Ojo con el origen de los datos, que no es el mismo: mi barra sale de Odoo
// (hr.attendance, que es lo que cuenta) y las de los demas del registro de ALM,
// que es lo unico que se puede leer de otra persona. Coinciden salvo en fichajes
// que Odoo tenga y aqui no se hayan traido todavia.
export default function HorasSemanales({ showToast, anio, mes: mesNum, lunesIso,
                                         onSemana, onMes, comparativa = null,
                                         miEmpleado = null, miJornada = JORNADA_DEFECTO }) {
  const mes = useMemo(() => ({ anio, mes: mesNum }), [anio, mesNum]);
  // Lunes de la semana que se está mirando. A mediodia: inmune al cambio de hora.
  const lunes = useMemo(() => lunesDe(new Date(`${lunesIso}T12:00:00`)), [lunesIso]);
  const setLunes = d => onSemana(ymd(d));
  // Plegado o no, recordado por navegador: debajo va el calendario del mes y
  // quien viene a mirarlo no quiere el panel empujandolo fuera de pantalla.
  const [plegado, setPlegado] = useState(() => {
    try { return localStorage.getItem(CLAVE_PLEGADO) === "1"; } catch { return false; }
  });
  const plegar = () => setPlegado(p => {
    try { localStorage.setItem(CLAVE_PLEGADO, p ? "0" : "1"); } catch { /* */ }
    return !p;
  });
  const [verMotivo, setVerMotivo] = useState(false);
  const [tramos, setTramos] = useState([]);
  // { 'AAAA-MM-DD': { tipo, motivo } } — festivos del calendario laboral y mis
  // vacaciones, permisos y bajas. Un dia asi no exige horas.
  const [fuera, setFuera] = useState({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [ahora, setAhora] = useState(Date.now());

  // Si al pasar de semana se sale del mes, el mes acompaña —y con el, el
  // calendario de abajo, que ahora lee el mismo estado—. Se mira si la semana
  // está EN la tira, no si comparte mes: la primera de cada mes suele empezar
  // en el anterior, y comparando meses la tira retrocedía sola al pulsarla.
  useEffect(() => {
    if (semanasMes.some(l => ymd(l) === lunesIso)) return;
    onMes(lunes.getFullYear(), lunes.getMonth());
  }, [lunesIso]); // eslint-disable-line

  // El tramo abierto crece mientras se mira: sin este tic, el día en curso se
  // quedaba congelado en la hora a la que se cargó la página.
  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // Semanas del mes de la tira: las que empiezan dentro de él, más la que lo
  // arrastra desde el mes anterior, que es la que contiene el día 1.
  const semanasMes = useMemo(() => {
    const primero = new Date(mes.anio, mes.mes, 1);
    const ultimo  = new Date(mes.anio, mes.mes + 1, 0);
    const out = [];
    for (let l = lunesDe(primero); l <= ultimo; l = sumarDias(l, 7)) out.push(l);
    return out;
  }, [mes]);

  // Se pide de una vez todo lo que se pinta: la semana elegida y el mes entero
  // de la tira. Partirlo en dos consultas solo dobla el viaje a Odoo.
  const desde = ymd(semanasMes[0] < lunes ? semanasMes[0] : lunes);
  const hasta = ymd(sumarDias(semanasMes[semanasMes.length - 1], 6) > sumarDias(lunes, 6)
                    ? sumarDias(semanasMes[semanasMes.length - 1], 6)
                    : sumarDias(lunes, 6));

  useEffect(() => {
    let vivo = true;
    setCargando(true); setError(null);
    api.getMisTramos(desde, hasta)
      .then(r => { if (vivo) { setTramos(r.tramos || []); setFuera(r.no_exigibles || {}); } })
      .catch(e => {
        if (!vivo) return;
        setTramos([]); setFuera({});
        // Sin Odoo conectado no es un fallo que merezca un aviso rojo: el
        // panel lo dice y ya está.
        if (e.code === "SIN_CONEXION") setError("Conecta tu cuenta de Odoo para ver tus horas.");
        else { setError("No se pudieron leer las horas de Odoo."); showToast?.(e.message || "Error al leer las horas", "error"); }
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [desde, hasta]); // eslint-disable-line

  // ── El año ─────────────────────────────────────────────────────────────────
  //
  // Va en su propia consulta porque el rango de arriba solo cubre la tira de
  // semanas. Es un viaje mas a Odoo, pero solo al cambiar de año: doce meses de
  // hr.attendance de una persona son unos cientos de filas.
  const [anual, setAnual] = useState(null);

  useEffect(() => {
    let vivo = true;
    const anio = mes.anio;
    setAnual(null);
    api.getMisTramos(`${anio}-01-01`, `${anio}-12-31`)
      .then(r => {
        if (!vivo) return;
        // Las horas de cada dia del año. El tramo abierto se cuenta contra el
        // reloj de ahora: aqui no hace falta el tic del minuto, sobre un año
        // esos segundos no cambian la cifra que se lee.
        const deDia = {};
        for (const t of (r.tramos || [])) {
          const h = t.salida ? t.horas : Math.max(0, (Date.now() - Date.parse(t.entrada)) / 3600000);
          deDia[t.dia] = (deDia[t.dia] || 0) + h;
        }
        const noExigibles = r.no_exigibles || {};
        const hoy = ymd(new Date());
        let horas = 0, objetivo = 0, exigido = 0, laborables = 0, sinJornada = 0;
        // Mes a mes, para poder decir donde esta el agujero y donde lo que sobra.
        const meses = [];
        for (let m = 0; m < 12; m++) {
          const ultimo = new Date(anio, m + 1, 0).getDate();
          const acc = { mes: m, horas: 0, objetivo: 0, exigido: 0 };
          for (let d = 1; d <= ultimo; d++) {
            const f = new Date(anio, m, d, 12);
            const clave = ymd(f);
            const base = objetivoDia(miJornada, clave);
            const finde = base === 0;
            // La misma regla que la semana y el mes: ni un dia fuera de mi jornada, ni
            // festivo, ni vacaciones, permiso o baja exigen horas.
            const obj = noExigibles[clave] ? 0 : base;
            acc.horas += deDia[clave] || 0;
            acc.objetivo += obj;
            if (obj > 0) laborables++;
            if (!finde && noExigibles[clave]) sinJornada++;
            if (clave < hoy) acc.exigido += obj;
          }
          horas += acc.horas;
          objetivo += acc.objetivo;
          exigido += acc.exigido;
          meses.push(acc);
        }

        // Solo entran los meses con dias ya cerrados: los que aun no han
        // llegado saldrian como el mayor excedente del año con cero horas.
        const cerrados = meses.filter(m => m.exigido > 0)
                              .map(m => ({ ...m, saldo: m.horas - m.exigido }));
        const peor  = cerrados.reduce((a, b) => (b.saldo < a.saldo ? b : a), cerrados[0] || null);
        const mejor = cerrados.reduce((a, b) => (b.saldo > a.saldo ? b : a), cerrados[0] || null);

        setAnual({ anio, horas, objetivo, exigido, laborables, sinJornada, peor, mejor });
      })
      // Un fallo aqui no debe tirar el panel: la semana y el mes siguen bien.
      .catch(() => { if (vivo) setAnual(null); });
    return () => { vivo = false; };
  }, [mes.anio, miJornada]);

  // Horas por día natural. El tramo todavía abierto se cuenta contra el reloj:
  // Odoo le pone worked_hours 0 hasta que se cierra y el día en curso salía a
  // cero teniendo media jornada hecha.
  const porDia = useMemo(() => {
    const m = {};
    for (const t of tramos) {
      const horas = t.salida
        ? t.horas
        : Math.max(0, (ahora - Date.parse(t.entrada)) / 3600000);
      m[t.dia] = (m[t.dia] || 0) + horas;
    }
    return m;
  }, [tramos, ahora]);

  // Horas que se le piden a un dia: ninguna en fin de semana, festivo o
  // ausencia. Es lo que hace que el saldo signifique algo en una semana con
  // dos festivos o con vacaciones a medias.
  const objetivoDe = clave => (fuera[clave] ? 0 : objetivoDia(miJornada, clave));

  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const f = sumarDias(lunes, i);
    const clave = ymd(f);
    const finde = miJornada.dias[i] === 0;
    return {
      indice: i, clave, etiqueta: DIAS[i], numero: f.getDate(),
      horas: porDia[clave] || 0,
      hoy: clave === ymd(new Date()),
      finde,
      ausencia: fuera[clave] || null,
      objetivo: objetivoDe(clave),
      tramos: tramos.filter(t => t.dia === clave),
    };
  }), [lunes, porDia, tramos, fuera, miJornada]); // eslint-disable-line

  const total      = dias.reduce((s, d) => s + d.horas, 0);
  const trabajados = dias.filter(d => d.horas > 0).length;
  const objetivo   = dias.reduce((s, d) => s + d.objetivo, 0);
  const saldo      = total - objetivo;
  const enCurso    = tramos.some(t => !t.salida);
  const librados   = dias.filter(d => !d.finde && d.ausencia);

  // En la semana en curso el saldo se mide contra lo exigido por los días YA
  // cerrados, no contra las 40 h: un lunes por la mañana el saldo salía en −38
  // horas, y contando el día de hoy como exigido, todo el viernes en negativo.
  const esSemanaActual = ymd(lunes) === ymd(lunesDe(new Date()));
  const hoyIso = ymd(new Date());
  const exigidoHoy = esSemanaActual
    ? dias.filter(d => d.clave < hoyIso).reduce((s, d) => s + d.objetivo, 0)
    : objetivo;
  const saldoHoy = total - exigidoHoy;

  const tendencia = useMemo(() => semanasMes.map(l => {
    const claves = Array.from({ length: 7 }, (_, j) => ymd(sumarDias(l, j)));
    return {
      lunes: l,
      horas: claves.reduce((s, c) => s + (porDia[c] || 0), 0),
      // Cada semana se juzga contra SU objetivo: la de Semana Santa no puede
      // salir en ambar por no llegar a 40 h.
      objetivo: claves.reduce((s, c) => s + objetivoDe(c), 0),
      actual: ymd(l) === ymd(lunes),
    };
  }), [semanasMes, lunes, porDia, fuera, miJornada]); // eslint-disable-line

  // El mes de la tira, en total. Se recorre por dias naturales y no sumando las
  // semanas de arriba: la primera y la ultima se salen del mes, y el objetivo
  // mensual saldria inflado con dias que no son de este mes.
  //
  // Los datos ya estan pedidos —el rango que se trae cubre la tira entera—, asi
  // que la cuenta no cuesta otro viaje a Odoo.
  const resumenMes = useMemo(() => {
    const ultimo = new Date(mes.anio, mes.mes + 1, 0).getDate();
    const hoy = ymd(new Date());
    let horas = 0, objetivo = 0, exigidoHoy = 0, sinJornada = 0, conFichaje = 0, laborables = 0;
    for (let d = 1; d <= ultimo; d++) {
      const f = new Date(mes.anio, mes.mes, d, 12);
      const clave = ymd(f);
      const finde = objetivoDia(miJornada, clave) === 0;
      const obj = objetivoDe(clave);
      const h = porDia[clave] || 0;
      horas += h;
      objetivo += obj;
      if (obj > 0) laborables++;
      // El dia en curso no se exige todavia: a media manana el saldo del mes
      // saldria en rojo por las horas que aun quedan por hacer hoy.
      if (clave < hoy) exigidoHoy += obj;
      if (!finde && fuera[clave]) sinJornada++;
      if (h > 0) conFichaje++;
    }
    return { horas, objetivo, exigidoHoy, sinJornada, conFichaje, laborables };
  }, [mes, porDia, fuera, miJornada]); // eslint-disable-line

  // De donde sale lo que se exige: la jornada de Odoo o el limite por defecto.
  const textoJornada = miJornada.origen === "odoo"
    ? `tu jornada de Odoo: ${fmtH(miJornada.horas_semana)} por semana`
    : `límite por defecto: ${fmtH(miJornada.horas_dia)} al día, ${fmtH(miJornada.horas_semana)} a la semana`
      + (miJornada.motivo_defecto ? `. ${explicarMotivo(miJornada.motivo_defecto)}` : "");

  const esMesActual = mes.anio === new Date().getFullYear() && mes.mes === new Date().getMonth();
  // El mes en curso se mide contra los dias ya cerrados; uno pasado, contra el
  // mes completo.
  const exigidoMes = esMesActual ? resumenMes.exigidoHoy : resumenMes.objetivo;
  const saldoMes   = resumenMes.horas - exigidoMes;

  // El desglose va en el title: lo que se mira es la cifra, y de donde sale se
  // consulta una vez.
  const detalleMes = [
    `${resumenMes.laborables} dias de jornada = ${resumenMes.objetivo} h del mes completo (${textoJornada})`,
    resumenMes.sinJornada
      ? `${resumenMes.sinJornada} dia${resumenMes.sinJornada === 1 ? "" : "s"} sin jornada exigible `
        + "(festivo, vacaciones, permiso o baja), ya descontados"
      : null,
    esMesActual ? `Exigido hasta ayer: ${exigidoMes} h - el dia en curso no cuenta todavia` : null,
    `${resumenMes.conFichaje} dia${resumenMes.conFichaje === 1 ? "" : "s"} con fichaje`,
  ].filter(Boolean).join("\n");

  const esAnioActual = anual?.anio === new Date().getFullYear();
  // Como en el mes: el año en curso se mide contra los dias ya cerrados, y uno
  // pasado contra el año completo.
  const exigidoAnio = anual ? (esAnioActual ? anual.exigido : anual.objetivo) : 0;
  const saldoAnio   = anual ? anual.horas - exigidoAnio : 0;
  const detalleAnio = anual ? [
    `${anual.laborables} dias de jornada = ${anual.objetivo} h del año completo (${textoJornada})`,
    anual.sinJornada
      ? `${anual.sinJornada} dia${anual.sinJornada === 1 ? "" : "s"} sin jornada exigible `
        + "(festivo, vacaciones, permiso o baja), ya descontados"
      : null,
    esAnioActual ? `Exigido hasta ayer: ${exigidoAnio} h - el dia en curso no cuenta todavia` : null,
  ].filter(Boolean).join("\n") : "";

  const gente = comparativa?.personas || [];

  // Media por persona de los equipos marcados, dia a dia. Ya no alimenta las
  // barras del dia —ahora hay una por persona— pero si la tira de semanas: doce
  // barras por semana en una tira de 26 px de alto no se leen.
  const cmpDia = clave => {
    const v = comparativa?.mediaDia?.[clave];
    return v === undefined || v === null ? null : v;
  };
  // La misma media, sumada por semana: la media por persona de esa semana.
  const cmpSemana = l => {
    if (!comparativa) return null;
    let hay = false, total = 0;
    for (let j = 0; j < 7; j++) {
      const v = cmpDia(ymd(sumarDias(l, j)));
      if (v !== null) { hay = true; total += v; }
    }
    return hay ? total : null;
  };

  // Horas que se le pintan a una persona un dia. Una ausencia aprobada se pinta
  // como jornada completa —con rayas, para que no se confunda con horas
  // fichadas—: si saliera a cero, quien estuvo de vacaciones apareceria como el
  // que menos trabajo de la semana, que es lo contrario de lo que pasó.
  const horasDe = (p, clave) => (p.ausenciaDia?.[clave]
    ? objetivoDia(p.jornada || JORNADA_DEFECTO, clave) : (p.horasDia?.[clave] || 0));

  // Un sabado o un domingo en el que nadie de los que se comparan ficho no es
  // informacion, es media tira de grafico gastada en dos columnas vacias. Los
  // festivos NO se ocultan: ahi la duda es justo por que no se ficho, y la
  // columna con su etiqueta lo contesta.
  // Un dia no laborable entre semana (el lunes de quien trabaja de martes a sabado) se
  // queda a la vista, rotulado: su hueco explica la semana. Solo se ocultan los del fin
  // de semana en los que nadie ha fichado.
  const diasVisibles = dias.filter(d => !d.finde || d.indice < 5 || d.horas > 0 || d.ausencia
                                     || gente.some(p => horasDe(p, d.clave) > 0));
  // Con jornadas distintas entre las personas comparadas, una cifra unica de "jornada completa"
  // mentiria: la leyenda lo dice con palabras.
  const jornadasIguales = gente.every(p => (p.jornada || JORNADA_DEFECTO).horas_dia === miJornada.horas_dia);
  const findesOcultos = dias.length - diasVisibles.length;

  // Los techos tienen que contar TODAS las barras: si no, la de quien haya
  // fichado mas que yo se salia de la caja.
  const cmpSemanas = tendencia.map(s => cmpSemana(s.lunes) || 0);
  const techo = Math.max(...miJornada.dias, ...diasVisibles.map(d => d.horas),
    ...diasVisibles.flatMap(d => gente.map(p => horasDe(p, d.clave))));
  const techoSemana = Math.max(miJornada.horas_semana, ...tendencia.map(s => s.horas), ...cmpSemanas);

  // Con mas de ocho barras por dia la foto no cabe: se cae a la leyenda, que
  // lleva las mismas caras y no depende del ancho de la columna.
  const cabenFotos = gente.length + 1 <= 8;
  // Las rayas de una ausencia: horas que no se han fichado. `c` puede ser cualquier
  // color CSS, tambien un var(--token): por eso el hueco es transparent y no un
  // sufijo hex pegado al color, que con un token da un gradiente invalido y no pinta nada. El borde
  // de 1 px del mismo color cierra el bloque y lo hace visible aunque las rayas
  // sean finas. Las mias van en el color propio de ausencia (--aus-*), distinto del
  // verde/ambar de las horas; las de quien comparo, en su color.
  const rayas = c => `repeating-linear-gradient(45deg, ${c} 0 2px, transparent 2px 6px)`;

  // Verde si el día cumple lo suyo, ámbar si se queda corto, y azul cuando se
  // ficha en un día que no exigía nada: son horas que suman, no un incumplimiento.
  const color = d => (!d.horas ? "var(--border)"
                    : !d.objetivo ? "var(--accent)"
                    : d.horas >= d.objetivo ? "var(--bar-ok)" : "var(--bar-warn)");

  const ETIQUETA_AUSENCIA = { festivo:"Festivo", vacaciones:"Vacaciones", permiso:"Permiso", baja:"Baja" };
  const nombreAusencia = a => ETIQUETA_AUSENCIA[a?.tipo] || "Sin jornada";

  const card = { background:"var(--surface)", borderRadius:13, border:"1px solid var(--border)",
                 boxShadow:"var(--shadow-sm)" };
  const rotulo = { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em",
                   color:"var(--text3)", marginBottom:3 };

  const rango = rangoSemana(lunes);
  const defecto = miJornada.origen !== "odoo";
  const fuente = defecto ? "límite por defecto" : "jornada de Odoo";

  return (
    <div data-jornada-origen={miJornada.origen} data-jornada-motivo={miJornada.motivo_defecto || undefined}
      style={{ ...card, padding: plegado ? "9px 14px" : "12px 15px 13px", marginBottom:12 }}>
      {/* Cabecera: qué semana y cómo va */}
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
                    marginBottom: plegado ? 0 : 11 }}>
        <Icon name="clock" size={14} color="var(--text3)"/>
        <span style={{ fontSize:13.5, fontWeight:700 }}>Mi jornada</span>
        <span style={{ fontSize:11.5, color:"var(--text3)" }}>{rango}</span>
        {cargando && <span style={{ fontSize:11, color:"var(--text3)" }}>Consultando Odoo…</span>}

        {/* Plegado, las dos cifras que se miran viven en la propia cabecera */}
        {plegado && !error && (
          <span style={{ display:"flex", alignItems:"center", gap:8, fontSize:12 }}>
            <b style={{ fontWeight:800 }}>{fmtHoras(total)}</b>
            <span style={{ fontWeight:800, color: saldoHoy >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
              {fmtSaldo(saldoHoy)}
            </span>
          </span>
        )}

        {/* Las flechas de semana y de mes viven arriba, en la barra de
            controles que este panel comparte con el calendario: tenerlas aqui
            tambien era la mitad del lio, porque cada juego movia solo su mitad
            de la pantalla. */}
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4 }}>
          <button aria-label={plegado ? "Desplegar" : "Plegar"} className="btn" title={plegado ? "Desplegar" : "Plegar"} onClick={plegar}
            style={{ padding:"5px 8px", borderRadius:8, border:"1px solid var(--border)",
                     background:"var(--surface2)", cursor:"pointer", lineHeight:0, order:3 }}>
            <Icon name={plegado ? "chevron-down" : "chevron-up"} size={11} color="var(--text3)"/>
          </button>
        </div>
      </div>

      {!plegado && (
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap",
                      margin:"-6px 0 10px", fontSize:11.5, color:"var(--text2)" }}>
          <span>{lineaJornada(miJornada)}</span>
          {defecto ? (
            <button type="button" className="btn" aria-expanded={verMotivo} onClick={() => setVerMotivo(v => !v)}
              style={{ padding:"2px 9px", minHeight:24, borderRadius:20, fontSize:11, fontWeight:700,
                       cursor:"pointer", border:"1px solid var(--warn-line)", background:"var(--warn-bg)",
                       color:"var(--warn-text)" }}>
              Límite por defecto ⓘ
            </button>
          ) : <span style={{ color:"var(--text3)" }}>según Odoo</span>}
          {defecto && verMotivo && (
            <div role="status" style={{ flexBasis:"100%", padding:"6px 9px", borderRadius:8, fontSize:11.5,
                                       background:"var(--surface2)", border:"1px solid var(--border)" }}>
              {explicarMotivo(miJornada.motivo_defecto)}
            </div>
          )}
        </div>
      )}

      {error ? (
        <div style={{ fontSize:12, color:"var(--text3)", padding:"14px 0" }}>{error}</div>
      ) : plegado ? null : (
        <>
          {/* Las cuatro cifras de la semana */}
          <div style={{ display:"flex", gap:20, flexWrap:"wrap", marginBottom:11 }}>
            <div>
              <div style={rotulo}>Horas fichadas</div>
              <div style={{ fontSize:19, fontWeight:800, letterSpacing:"-.02em",
                            color: total >= exigidoHoy ? "var(--ok-text)" : "var(--text)" }}>
                {fmtHoras(total)}
              </div>
              <div style={{ fontSize:11, color:"var(--text3)" }}>
                de {objetivo} h ({fuente}) {enCurso && "· tramo en curso"}
              </div>
            </div>

            <div>
              <div style={rotulo}>{esSemanaActual ? "Saldo a hoy" : "Saldo"}</div>
              <div style={{ fontSize:19, fontWeight:800, letterSpacing:"-.02em",
                            color: saldoHoy >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                {fmtSaldo(saldoHoy)}
              </div>
              <div style={{ fontSize:11, color:"var(--text3)" }}>
                {esSemanaActual ? `sobre ${exigidoHoy} h exigidas en los días cerrados` : `sobre ${objetivo} h (${fuente})`}
              </div>
            </div>

            <div>
              <div style={rotulo}>Días con fichaje</div>
              <div style={{ fontSize:19, fontWeight:800, letterSpacing:"-.02em" }}>{trabajados}</div>
              <div style={{ fontSize:11, color:"var(--text3)" }}>
                {librados.length
                  ? `media ${trabajados ? fmtHoras(total / trabajados) : "—"} · ${librados.length} sin jornada`
                  : `media ${trabajados ? fmtHoras(total / trabajados) : "—"}`}
              </div>
            </div>

            {/* En la semana en curso esto no es un incumplimiento: es la
                jornada que todavia queda por hacer. Un lunes por la mañana
                marcaba "−31h 44m · faltan 31h 44m", que se lee como una alarma
                cuando solo significa que quedan cuatro dias por delante. */}
            <div>
              <div style={rotulo}>{esSemanaActual ? "Queda esta semana" : "Cierre de semana"}</div>
              <div style={{ fontSize:19, fontWeight:800, letterSpacing:"-.02em",
                            color: saldo >= 0 ? "var(--ok-text)" : "var(--text)" }}>
                {esSemanaActual ? fmtHoras(Math.max(0, -saldo)) : fmtSaldo(saldo)}
              </div>
              <div style={{ fontSize:11, color:"var(--text3)" }}>
                {saldo >= 0 ? "objetivo cubierto"
                  : esSemanaActual ? `para llegar a las ${objetivo} h`
                  : `faltaron ${fmtHoras(-saldo)}`}
              </div>
            </div>
          </div>

          {/* Un día por columna. La línea punteada es la jornada del día */}
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", padding:"0 2px" }}>
            {diasVisibles.map(d => {
              // Mi barra primero y las de los demas detras, siempre en el orden
              // de la leyenda: si cambiara de un dia a otro no habria
              // comparacion posible, solo un rebujo de colores.
              const barras = [
                { clave:"yo", nombre:"Yo", color:color(d), empId:miEmpleado, yo:true,
                  horas:d.horas, ausencia:d.ausencia?.tipo || null,
                  // La ausencia de un festivo no se pinta como jornada: la
                  // tiene todo el mundo y llenaria el dia de barras rayadas.
                  comoJornada: !!d.ausencia && d.ausencia.tipo !== "festivo",
                  jornadaDia: objetivoDia(miJornada, d.clave) },
                ...gente.map(p => ({
                  clave:p.uid, nombre:p.nombre, color:p.color, empId:p.empId, yo:false,
                  horas: p.horasDia?.[d.clave] || 0,
                  ausencia: p.ausenciaDia?.[d.clave] || null,
                  comoJornada: !!p.ausenciaDia?.[d.clave],
                  jornadaDia: objetivoDia(p.jornada || JORNADA_DEFECTO, d.clave),
                })),
              ];
              return (
              <div key={d.clave}
                style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                         minWidth:0 }}>
                <div title={[
                      d.ausencia ? `${nombreAusencia(d.ausencia)}${d.ausencia.motivo ? `: ${d.ausencia.motivo}` : ""} · no exige jornada` : null,
                      ...(d.tramos.length
                        ? d.tramos.map(t => `${hhmm(t.entrada, "--:--")} → ${t.salida ? hhmm(t.salida, "--:--") : "en curso"}`)
                        : ["Sin fichajes"]),
                    ].filter(Boolean).join("\n")}
                  style={{ fontSize:11, fontWeight:700,
                           color: d.horas ? "var(--text2)" : "var(--text3)" }}>
                  {d.horas ? fmtHoras(d.horas) : "—"}
                  {/* El color de la barra (verde, ambar, gris) tambien en texto */}
                  <span className="sr-only">{!d.horas ? ", sin horas" : !d.objetivo ? ", horas en un día sin jornada" : d.horas >= d.objetivo ? ", jornada cumplida" : ", por debajo de la jornada"}</span>
                </div>
                {/* La rayita es la jornada del dia. Va dentro de la barra y no
                    como linea suelta del contenedor: asi no hay que adivinar
                    cuanto ocupan los rotulos para cuadrarla. */}
                <div style={{ width:"100%", height:56, display:"flex", alignItems:"flex-end",
                              gap:2, position:"relative" }}>
                  {/* La rayita solo tiene sentido donde se exige jornada */}
                  {d.objetivo > 0 && (
                    <div style={{ position:"absolute", left:0, right:0,
                                  bottom:`${(d.objetivo / techo) * 100}%`,
                                  borderTop:"1px dashed var(--border)", pointerEvents:"none" }}/>
                  )}
                  {/* Un dia sin jornada exigible se marca de fondo aunque no se
                      haya fichado: si no, un festivo y un dia perdido se veian
                      igual, los dos con la barra a cero. */}
                  {(d.ausencia || (d.finde && !d.horas)) && (
                    <div style={{ position:"absolute", inset:0, borderRadius:6,
                                  background:"var(--surface2)", border:"1px dashed var(--border)" }}/>
                  )}
                  {/* Una barra por persona, al lado y no detras: encima se leian
                      como una sola barra de varios colores y no se sabia cual
                      era cual. */}
                  {barras.map(b => {
                    const alto = b.comoJornada ? b.jornadaDia : b.horas;
                    return (
                      <div key={b.clave} title={`${b.nombre}: `
                            + (b.comoJornada
                               ? `${ETIQUETA_AUSENCIA[b.ausencia] || "sin jornada"} · se pinta como `
                                 + `jornada completa de ${fmtH(b.jornadaDia)}, no son horas fichadas`
                               : b.horas > 0 ? fmtHoras(b.horas) : "sin fichajes")}
                        // position:relative: el fondo de un dia sin jornada (arriba) es absolute y
                        // un elemento posicionado se pinta ENCIMA de los que no lo estan. Sin esto,
                        // en un dia de ausencia el fondo tapaba las barras de todos.
                        style={{ position:"relative", flex:1, minWidth:2,
                                 height:`${Math.max(2, (alto / techo) * 100)}%`,
                                 borderRadius:"6px 6px 3px 3px",
                                 background: b.comoJornada
                                   ? `${rayas(b.yo ? "var(--aus-line)" : b.color)}, ${b.yo ? "var(--aus-bg)" : "transparent"}`
                                   : b.color,
                                 border: b.comoJornada ? `1px solid ${b.yo ? "var(--aus-line)" : b.color}` : "none",
                                 opacity: !b.yo && !alto ? .25 : d.finde && !alto ? .35 : 1,
                                 transition:"height .2s" }}/>
                    );
                  })}
                </div>
                {/* La cara de cada barra, al pie y alineada con ella: con seis
                    personas delante, el color solo no basta para saber de quien
                    es la columna que se sale. */}
                {cabenFotos && gente.length > 0 && (
                  <div style={{ display:"flex", gap:2, width:"100%" }}>
                    {barras.map(b => (
                      <div key={b.clave} style={{ flex:1, minWidth:0, display:"flex",
                                                  justifyContent:"center" }}>
                        <AvatarOdoo modelo="hr.employee" id={b.empId} nombre={b.nombre} size={15}/>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize:11, fontWeight: d.hoy ? 800 : 600, textAlign:"center",
                              color: d.hoy ? "var(--accent)" : d.finde ? "var(--text3)" : "var(--text2)" }}>
                  {d.etiqueta} {d.numero}
                </div>
                {d.finde && !d.ausencia && (
                  <div style={{ fontSize:11, fontWeight:800, color:"var(--text2)", whiteSpace:"nowrap" }}>
                    No laborable
                  </div>
                )}
                {d.ausencia && (
                  <div style={{ fontSize:11, fontWeight:800,
                                color: d.ausencia.tipo === "festivo" ? "var(--text3)" : "var(--aus-text)",
                                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                                maxWidth:"100%" }}>
                    {nombreAusencia(d.ausencia)}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* Quien es cada barra. Las fotos al pie de cada columna no llevan
              nombre —no cabe—, y las rayas de una ausencia hay que explicarlas
              una vez. Los findes ocultos se dicen aqui: una columna que
              desaparece sin avisar se lee como un fallo. */}
          {(gente.length > 0 || findesOcultos > 0) && (
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
                          marginTop:8, fontSize:11, color:"var(--text3)" }}>
              {gente.length > 0 && (
                <span style={{ display:"flex", alignItems:"center", gap:5, fontWeight:700,
                               color:"var(--text2)" }}>
                  <i style={{ width:9, height:9, borderRadius:2, background:"var(--bar-ok)" }}/>
                  Yo
                </span>
              )}
              {gente.map(p => (
                <span key={p.uid} style={{ display:"flex", alignItems:"center", gap:4 }}
                  title={`${p.nombre} · ${p.equipo || "sin equipo"}`}>
                  <AvatarOdoo modelo="hr.employee" id={p.empId} nombre={p.nombre} size={14}/>
                  <i style={{ width:9, height:9, borderRadius:2, background:p.color }}/>
                  {p.nombre}
                </span>
              ))}
              {gente.some(p => Object.keys(p.ausenciaDia || {}).length) && (
                <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <i style={{ width:11, height:9, borderRadius:2, border:"1px solid var(--aus-line)",
                              background:`${rayas("var(--aus-line)")}, var(--aus-bg)` }}/>
                  rayado: vacaciones, permiso o baja aprobados, contados como {jornadasIguales ? fmtH(miJornada.horas_dia) : "la jornada de cada persona ese día"}
                </span>
              )}
              {!jornadasIguales && (
                <span style={{ flexBasis:"100%", color:"var(--text2)" }}>
                  Las jornadas difieren: cada persona se mide contra la suya (yo {fmtH(miJornada.horas_semana)}/sem
                  {gente.map(p => `, ${p.nombre} ${fmtH((p.jornada || JORNADA_DEFECTO).horas_semana)}/sem`).join("")}).
                </span>
              )}
              {findesOcultos > 0 && (
                <span style={{ marginLeft:"auto" }}>
                  {findesOcultos === 1 ? "1 día de fin de semana oculto"
                                       : `${findesOcultos} días de fin de semana ocultos`}: nadie fichó
                </span>
              )}
            </div>
          )}

          {/* Las semanas del mes. El mes se elige arriba, en la barra de
              controles que comparte con el calendario. */}
          <div style={{ marginTop:11, borderTop:"1px solid var(--border2)", paddingTop:9 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              <span style={{ fontSize:11.5, fontWeight:700, textTransform:"capitalize" }}>
                Semanas de {MESES[mes.mes]} {mes.anio}
              </span>
              <span className="m-hide" style={{ fontSize:11, color:"var(--text3)" }}>
                pulsa una semana para verla arriba y marcarla en el calendario
              </span>

              {/* Lo exigible del mes: es la cuenta que se hace al cerrarlo
                  —horas que tocaban, fichadas y diferencia— y hasta ahora habia
                  que sacarla sumando las semanas a mano. */}
              <span style={{ marginLeft:"auto", display:"flex", alignItems:"baseline", gap:7,
                             fontSize:11, flexWrap:"wrap" }}
                title={detalleMes}>
                <span style={{ color:"var(--text3)" }}>{esMesActual ? "Mes a hoy" : "Mes"}</span>
                <b style={{ fontWeight:800 }}>{fmtHoras(resumenMes.horas)}</b>
                <span style={{ color:"var(--text3)" }}>de {exigidoMes} h</span>
                <span style={{ fontWeight:800, color: saldoMes >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                  {fmtSaldo(saldoMes)}
                </span>
                {esMesActual && resumenMes.objetivo !== exigidoMes && (
                  <span style={{ color:"var(--text3)" }}>
                    · mes completo {resumenMes.objetivo} h
                  </span>
                )}
              </span>
            </div>
            <div style={{ display:"flex", gap:6, alignItems:"flex-end", marginTop:5 }}>
              {tendencia.map(s => {
                // La primera semana suele arrancar en el mes anterior: se avisa
                // en la etiqueta para que no parezca un error del calendario.
                return (
                <div key={ymd(s.lunes)} style={{ flex:1, display:"flex", flexDirection:"column",
                                                 alignItems:"center", gap:3, cursor:"pointer",
                                                 padding:"3px 2px", borderRadius:7,
                                                 background: s.actual ? "var(--accent-bg)" : "transparent",
                                                 border:`1px solid ${s.actual ? "var(--accent)" : "transparent"}` }}
                  title={`Semana del ${s.lunes.toLocaleDateString("es-ES", { day:"numeric", month:"long" })}`
                         + ` · ${fmtHoras(s.horas)} de ${s.objetivo} h`
                         + (cmpSemana(s.lunes) !== null
                            ? `
${comparativa.etiqueta}: media de ${fmtHoras(cmpSemana(s.lunes))} por persona`
                            : "")}
                  onMouseEnter={e => { if (!s.actual) e.currentTarget.style.background = "var(--bg)"; }}
                  onMouseLeave={e => { if (!s.actual) e.currentTarget.style.background = "transparent"; }}
                  onClick={() => setLunes(lunesDe(s.lunes))}>
                  <div style={{ width:"100%", height:26, display:"flex", alignItems:"flex-end",
                                gap:2 }}>
                    <div style={{ flex:1, height:`${Math.max(2, (s.horas / techoSemana) * 100)}%`,
                                  borderRadius:"4px 4px 2px 2px",
                                  background: s.actual ? "var(--accent)"
                                            : s.horas >= s.objetivo ? "var(--bar-ok)" : "var(--bar-warn)",
                                  opacity: s.horas ? 1 : .3 }}/>
                    {cmpSemana(s.lunes) !== null && (
                      <div style={{ flex:1, height:`${Math.max(2, (cmpSemana(s.lunes) / techoSemana) * 100)}%`,
                                    borderRadius:"4px 4px 2px 2px", background:"var(--text3)",
                                    opacity:.34 }}/>
                    )}
                  </div>
                  <div style={{ fontSize:11, color: s.actual ? "var(--accent)" : "var(--text3)",
                                fontWeight: s.actual ? 800 : 600, whiteSpace:"nowrap" }}>
                    {rangoSemanaCorto(s.lunes, mes.mes)}
                  </div>
                </div>
                );
              })}
            </div>

            {/* El año, en una linea: mismas tres cifras que el mes para que la
                cuenta se lea igual en las tres escalas. */}
            <div style={{ marginTop:9, borderTop:"1px solid var(--border2)", paddingTop:8,
                          display:"flex", alignItems:"baseline", gap:7, flexWrap:"wrap",
                          fontSize:11 }}
              title={detalleAnio}>
              <span style={{ fontWeight:700 }}>Año {mes.anio}</span>
              {anual ? (
                <>
                  <span style={{ color:"var(--text3)" }}>{esAnioActual ? "a hoy" : "cerrado"}</span>
                  <b style={{ fontWeight:800 }}>{fmtHoras(anual.horas)}</b>
                  <span style={{ color:"var(--text3)" }}>de {exigidoAnio} h</span>
                  <span style={{ fontWeight:800, color: saldoAnio >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                    {fmtSaldo(saldoAnio)}
                  </span>
                  {esAnioActual && anual.objetivo !== exigidoAnio && (
                    <span style={{ color:"var(--text3)" }}>· año completo {anual.objetivo} h</span>
                  )}

                  {/* Donde esta el agujero y donde lo que sobra. Con el saldo
                      del año a secas no se sabia de que mes venia, y era lo
                      primero que se preguntaba. Se pulsa y la tira va a ese mes.
                      Si el año entero va en negativo, el "mejor" es el mes que
                      menos falta: por eso lleva su propio signo y no un color
                      fijo. */}
                  {anual.peor && anual.mejor && anual.peor.mes !== anual.mejor.mes && (
                    <span style={{ display:"flex", alignItems:"baseline", gap:7, marginLeft:4 }}>
                      {[["Mayor falta", anual.peor], ["Mayor excedente", anual.mejor]].map(
                        ([rotulo, m]) => (
                          <button key={rotulo} className="btn"
                            title={`${rotulo} del año: ${fmtHoras(m.horas)} de ${m.exigido} h exigidas`
                                   + " · pulsa para ver el mes"}
                            onClick={() => onMes(anual.anio, m.mes)}
                            style={{ display:"flex", alignItems:"baseline", gap:5, padding:"2px 7px",
                                     borderRadius:7, border:"1px solid var(--border)",
                                     background:"var(--surface2)", cursor:"pointer",
                                     fontFamily:"var(--font)", fontSize:11 }}>
                            <span style={{ color:"var(--text3)" }}>{rotulo}</span>
                            <span style={{ fontWeight:700, textTransform:"capitalize" }}>
                              {MESES[m.mes]}
                            </span>
                            <span style={{ fontWeight:800,
                                           color: m.saldo >= 0 ? "var(--ok-text)" : "var(--red-text)" }}>
                              {fmtSaldo(m.saldo)}
                            </span>
                          </button>
                        ))}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color:"var(--text3)" }}>consultando Odoo…</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
